import React, { useCallback } from 'react';
import { Box, Snackbar, Alert, Fab, CircularProgress, Tooltip } from '@mui/material';
import UploadIcon from '@mui/icons-material/UploadFile';
import SendIcon from '@mui/icons-material/Send';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useOutletContext } from 'react-router-dom';

import styles from './MeterChanges.styles';
import MeterChangeHeader from '../../components/MeterChange/MeterChangeHeader';
import MeterChangeTable from '../../components/MeterChange/MeterChangeTable';
import MeterChangeImport from '../../components/MeterChange/MeterChangeImport';
import usePendingMeterChangeRows from '../../hooks/usePendingMeterChangeRows';
import useValidateMeterChanges from '../../hooks/useValidateMeterChanges';
import useProcessMeterChanges from '../../hooks/useProcessMeterChanges';
import useToast from '../../hooks/useToast';

export default function MeterChanges() {
  const { isSidebarExpanded } = useOutletContext?.() || { isSidebarExpanded: true };
  const { toast, setToast, close } = useToast();
  const { pending, setPending, addMany, removeAt, clear } = usePendingMeterChangeRows();
  const { validateAll } = useValidateMeterChanges({ setToast, setPending });
  const { processing, processValidated } = useProcessMeterChanges({ setToast });

  const handleRemove = useCallback((idx) => {
    if (typeof idx !== 'number') return;
    removeAt(idx);
  }, [removeAt]);

  const handleValidateAll = useCallback(() => {
    validateAll(pending);
  }, [validateAll, pending]);

  const handleProcess = useCallback(async () => {
    const rowsToSend = pending.filter((r) => r.Validated);
    const res = await processValidated(rowsToSend);
    if (res?.ok) clear();
  }, [pending, processValidated, clear]);

  const allValidated = pending.length > 0 && pending.every((r) => r.Validated);

  return (
    <>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          ...styles.MeterChangeDetails,
          marginLeft: isSidebarExpanded ? '240px' : '95px',
          width: isSidebarExpanded ? 'calc(100vw - 240px)' : 'calc(100vw - 95px)',
          transition: 'all 0.3s ease',
        }}
      >
        <Box sx={styles.headerRow}>
          <Box sx={styles.headerBar}>
            <MeterChangeHeader title="Meter Changes" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip title="Import meter changes" arrow>
                <span>
                  <Fab
                    data-testid="import-btn"
                    size="small"
                    sx={styles.fabButton}
                    onClick={() => document.getElementById('file-import-mc').click()}
                    aria-label="Import"
                  >
                    <UploadIcon fontSize="small" />
                  </Fab>
                </span>
              </Tooltip>

              <Tooltip title="Validate all" arrow>
                <span>
                  <Fab
                    data-testid="validate-btn"
                    size="small"
                    sx={styles.fabButton}
                    onClick={handleValidateAll}
                    disabled={!pending.length}
                    aria-label="Validate"
                  >
                    <VerifiedIcon fontSize="small" />
                  </Fab>
                </span>
              </Tooltip>

              <Tooltip title={processing ? 'Processing…' : 'Process validated rows'} arrow>
                <span>
                  <Fab
                    data-testid="process-btn"
                    size="small"
                    sx={styles.fabButton}
                    onClick={handleProcess}
                    disabled={!allValidated || processing}
                    aria-label="Process"
                  >
                    {processing ? (
                      <CircularProgress data-testid="process-loader" size={18} sx={{ color: 'inherit' }} />
                    ) : (
                      <SendIcon fontSize="small" />
                    )}
                  </Fab>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <MeterChangeImport
          onImport={(r) => {
            const { success, message, rows } = r;
            setToast({ open: true, message, severity: success ? 'success' : 'error' });
            if (success) addMany(rows);
          }}
        />

        <MeterChangeTable
          rows={pending}
          onRemove={handleRemove}
        />
      </Box>
    </>
  );
}
